// NOAA "subordinate" stations (e.g. Imperial Beach, National City, Quarantine
// Station) only publish high/low predictions — hourly requests fail. These
// helpers rebuild an hourly curve from the high/low turning points.

export const HILO_ONLY_STATIONS = new Set(["9410120", "9410152", "9410166"]);

export type HourlyPoint = { time: string; hour: number; tide: number };

const pad = (n: number) => String(n).padStart(2, "0");
const ymd = (d: Date) => `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;

/** Fetch hilo for day-1..day+1 and cosine-interpolate 24 hourly points for `dateStr` (YYYYMMDD). */
export async function fetchHourlyFromHilo(
  stationId: string,
  dateStr: string,
  fetcher: (url: string) => Promise<Response> = (u) => fetch(u),
): Promise<HourlyPoint[] | null> {
  const y = +dateStr.slice(0, 4), m = +dateStr.slice(4, 6) - 1, d = +dateStr.slice(6, 8);
  const day = new Date(y, m, d);
  const begin = ymd(new Date(y, m, d - 1));
  const end = ymd(new Date(y, m, d + 1));
  const url = `https://api.tidesandcurrents.noaa.gov/api/prod/datagetter?begin_date=${begin}&end_date=${end}&station=${stationId}&product=predictions&datum=MLLW&units=english&time_zone=lst_ldt&interval=hilo&format=json&application=SanDiegoCoast`;
  const res = await fetcher(url);
  if (!res.ok) return null;
  const data = await res.json();
  if (!data?.predictions?.length) return null;
  const pts = data.predictions.map((p: { t: string; v: string }) => {
    const [ds, ts] = p.t.split(" ");
    const [yy, mm, dd] = ds.split("-").map(Number);
    const [hh, mi] = ts.split(":").map(Number);
    return { ms: new Date(yy, mm - 1, dd, hh, mi).getTime(), v: parseFloat(p.v) };
  });
  const out: HourlyPoint[] = [];
  for (let h = 0; h < 24; h++) {
    const t = new Date(day.getFullYear(), day.getMonth(), day.getDate(), h).getTime();
    const i = pts.findIndex((p: { ms: number }) => p.ms > t);
    if (i <= 0) continue;
    const a = pts[i - 1], b = pts[i];
    const f = (t - a.ms) / (b.ms - a.ms);
    const v = a.v + (b.v - a.v) * (1 - Math.cos(Math.PI * f)) / 2;
    out.push({ time: `${pad(h)}:00`, hour: h, tide: parseFloat(v.toFixed(3)) });
  }
  return out.length ? out : null;
}
